import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { logInfo } from "@tools/LogsHandler.tsx";
import {
    ColorsParameterMap,
    ParameterType,
} from "@theme/types/colors.types.tsx";
import { Button, Col, Form, message, Modal, Row, Select, Space, TimePicker } from "antd";
import {
    getOrderedPaletteColors,
    opAspectRatio,
    mergePaletteWithRemote,
    applyPaletteToDom,
    applyHarmonyPaletteToDom,
    convertHarmonyToPalette,
} from "@theme/utils/themeUtils.tsx";
import { ColorPaletteConfig } from "@theme/types/colorHarmony.types.ts";
import { ColorHarmonyPicker } from "@theme/components/ColorHarmonyPicker.tsx";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store.tsx";
import { HorariosDisponiblesType, setAspectRadio } from "@store/slices/eshopSlice.tsx";
import { Dayjs } from "dayjs";
import { conns } from '@cfg/axiosConfig.tsx';
import { PaletaColoresSegmentada } from '@assets/svg/PaletaColoresSegmentada.tsx';
import { updateSiteSettings, type SiteSettings } from '@store/slices/settingsSlice.tsx';
import { selectIsStaffAdmin } from '@store/slices/userSlice.tsx';

const { Option } = Select;

// Unused legacy constant removed

const readHarmonyConfig = (): ColorPaletteConfig | null => {
    return null;
};

const writeHarmonyConfig = (_config: ColorPaletteConfig | null) => {
    // No-op to prevent unsaved theme configurations from polluting local storage cache
};

const clonePalette = (palette: ColorsParameterMap): ColorsParameterMap => {
    const next: ColorsParameterMap = {};
    Object.entries(palette).forEach(([key, element]) => {
        if (element) {
            next[key as any] = { ...element };
        }
    });
    return next;
};

interface AspectRatioSelectProps {
    initialAspect: string;
    onChange: (value: string) => void;
}

const AspectRatioSelect: React.FC<AspectRatioSelectProps> = ({ initialAspect, onChange }) => {
    const [selectedAspect, setSelectedAspect] = useState<string>(initialAspect);

    useEffect(() => {
        setSelectedAspect(initialAspect);
    }, [initialAspect]);

    const handleChange = (value: string) => {
        setSelectedAspect(value);
        onChange(value);
    };

    return (
        <Select value={selectedAspect} onChange={handleChange} style={{ width: 200 }}>
            {Object.entries(opAspectRatio).map(([key, label]) => (
                <Option key={key} value={key}>
                    {label}
                </Option>
            ))}
        </Select>
    );
};


export const PanelConfigEstilo: React.FC<{
    onSave: (colors: ColorsParameterMap) => void;
    initialPalette?: ColorsParameterMap | null;
    initialHarmonyConfig?: ColorPaletteConfig | null;
    onHarmonyChange?: (config: ColorPaletteConfig) => void;
}> = ({ onSave, initialPalette, initialHarmonyConfig, onHarmonyChange }) => {
    const [palette, setPalette] = useState<ColorsParameterMap>({});
    // Priority: prop from backend > localStorage > null
    const [harmonyConfig, setHarmonyConfig] = useState<ColorPaletteConfig | null>(() =>
        initialHarmonyConfig ?? readHarmonyConfig()
    );

    // Sync harmonyConfig when initialHarmonyConfig prop arrives from backend (async)
    useEffect(() => {
        if (initialHarmonyConfig && !harmonyConfig) {
            logInfo('[DEBUG] PanelConfigEstilo - Received initialHarmonyConfig from backend:', JSON.stringify(initialHarmonyConfig));
            setHarmonyConfig(initialHarmonyConfig);
        } else if (initialHarmonyConfig && harmonyConfig) {
            // Backend has config and we have local state - backend takes priority if different
            const localFallback = ['#f6f6e8', '#fcfaf7', '#ffffff', '#1677FF'];
            const isLocalStale = localFallback.some(fc => fc.toLowerCase() === harmonyConfig.baseColor1?.toLowerCase());
            if (isLocalStale) {
                logInfo('[DEBUG] PanelConfigEstilo - Local state is stale, using backend config:', JSON.stringify(initialHarmonyConfig));
                setHarmonyConfig(initialHarmonyConfig);
            }
        }
    }, [initialHarmonyConfig]); // Only run when prop changes, not on every harmonyConfig change

    useEffect(() => {
        logInfo('[DEBUG] PanelConfigEstilo - initialPalette received:', JSON.stringify(initialPalette, null, 2));
        const merged = mergePaletteWithRemote({}, initialPalette ?? null);
        logInfo('[DEBUG] PanelConfigEstilo - merged palette keys:', Object.keys(merged).join(', '));
        setPalette(merged);
        applyPaletteToDom(merged);
    }, [initialPalette]);

    useEffect(() => {
        onSave(palette);
        applyPaletteToDom(palette);
    }, [palette, onSave]);

    const orderedLightColors = useMemo(() => {
        const colors = getOrderedPaletteColors(palette);
        logInfo('[DEBUG] PanelConfigEstilo - orderedLightColors calculated:', colors);
        return colors;
    }, [palette]);
    const defaultHarmonyConfig = useMemo<ColorPaletteConfig>(
        () => {
            const config = {
                harmony: 'custom' as const,
                colorCount: 1 as const,
                baseColor1: orderedLightColors[0] ?? '#1677FF',
            };
            logInfo('[DEBUG] PanelConfigEstilo - defaultHarmonyConfig calculated:', JSON.stringify(config, null, 2));
            return config;
        },
        [orderedLightColors],
    );




    useEffect(() => {
        logInfo('[DEBUG] PanelConfigEstilo - harmonyConfig check. Current:', harmonyConfig ? JSON.stringify(harmonyConfig) : 'null');
        logInfo('[DEBUG] PanelConfigEstilo - orderedLightColors:', orderedLightColors);

        if (!harmonyConfig) {
            // No config at all - use default
            logInfo('[DEBUG] PanelConfigEstilo - Setting harmonyConfig to defaultHarmonyConfig');
            setHarmonyConfig(defaultHarmonyConfig);
        } else if (harmonyConfig.harmony === 'custom') {
            // In custom mode - check if we need to sync colors from palette
            const fallbackColors = ['#f6f6e8', '#fcfaf7', '#ffffff', '#1677FF'];
            const isStaleLocalStorage = fallbackColors.some(fc => fc.toLowerCase() === harmonyConfig.baseColor1?.toLowerCase());

            if (isStaleLocalStorage && orderedLightColors[0]) {
                // Determine how many distinct colors are in the palette
                const distinctColors = orderedLightColors.filter((c, i, arr) =>
                    c && arr.indexOf(c) === i && !fallbackColors.some(fc => fc.toLowerCase() === c.toLowerCase())
                ).slice(0, 3);

                const colorCount = Math.max(1, Math.min(3, distinctColors.length)) as 1 | 2 | 3;

                logInfo('[DEBUG] PanelConfigEstilo - Syncing from stale localStorage. Distinct palette colors:', distinctColors, 'colorCount:', colorCount);

                setHarmonyConfig({
                    harmony: 'custom',
                    colorCount: colorCount,
                    baseColor1: distinctColors[0] ?? orderedLightColors[0],
                    baseColor2: distinctColors[1],
                    baseColor3: distinctColors[2],
                });
            }
        }
    }, [harmonyConfig, defaultHarmonyConfig, orderedLightColors]);

    const handleHarmonyApply = (config: ColorPaletteConfig) => {
        setHarmonyConfig(config);
        writeHarmonyConfig(config);
        applyHarmonyPaletteToDom(config);
        onHarmonyChange?.(config);

        // Update palette state to match harmony config so it can be saved
        const newPalette = convertHarmonyToPalette(config, palette);
        setPalette(newPalette);
    };

    const handleHarmonyReset = () => {
        writeHarmonyConfig(null);
        setHarmonyConfig(defaultHarmonyConfig);
        applyPaletteToDom(palette);
    };

    return (
        <div className="w-100 d-flex flex-column gap-3" aria-label="Configuración de paleta">
            <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                <ColorHarmonyPicker
                    value={harmonyConfig ?? defaultHarmonyConfig}
                    onChange={setHarmonyConfig}
                    onLivePreview={applyHarmonyPaletteToDom}
                    onApply={handleHarmonyApply}
                    onReset={handleHarmonyReset}
                />
            </Space>
        </div>
    );
};

export interface ButtonPanelConfigEstiloProps {
    variant?: 'button' | 'embedded';
    className?: string;
    onAfterSave?: () => void;
    showSaveButton?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
}

export interface ButtonPanelConfigEstiloRef {
    saveColors: (additionalSettings?: Partial<SiteSettings>) => Promise<void>;
    restoreColors: () => void;
}

export const ButtonPanelConfigEstilo = forwardRef<
    ButtonPanelConfigEstiloRef,
    ButtonPanelConfigEstiloProps
>(({
    variant = 'button',
    className,
    onAfterSave,
    onDirtyChange,
}, ref) => {
    const isEmbedded = variant === 'embedded';
    const [isModalOpen, setIsModalOpen] = useState(isEmbedded);
    const [palette, setPalette] = useState<ColorsParameterMap>({});
    const [persistedPalette, setPersistedPalette] = useState<ColorsParameterMap>({});
    const [isSavingAppearance, setIsSavingAppearance] = useState(false);
    const item_aspect_card = useSelector((state: RootState) => state.eshop.cfg.item_aspect_card);
    const [prevAspect, setPrevAspect] = useState(item_aspect_card);
    const siteColors = useSelector((state: RootState) => state.settings.site?.site_colors);
    const siteHarmonyConfig = useSelector((state: RootState) => state.settings.site?.site_colors_harmony);
    const [currentHarmonyConfig, setCurrentHarmonyConfig] = useState<ColorPaletteConfig | null>(null);
    const isSuperuser = useSelector((state: RootState) => state.user.isSuperuser);
    const isStaffAdmin = useSelector(selectIsStaffAdmin);
    const userPermissions = useSelector((state: RootState) => state.user.permissions);
    const dispatch = useAppDispatch();
    const canManageColors = isSuperuser
        || isStaffAdmin
        || userPermissions.includes('roles:manage')
        || userPermissions.includes('settings:write');

    const showModal = () => {
        if (!isEmbedded) {
            setIsModalOpen(true);
        }
    };

    useEffect(() => {
        const merged = mergePaletteWithRemote({}, siteColors ?? null);
        const cloned = clonePalette(merged);
        setPalette(cloned);
        setPersistedPalette(clonePalette(cloned));
        applyPaletteToDom(cloned);
    }, [siteColors]);

    // Check for dirty state
    useEffect(() => {
        // Compare palettes ignoring has_change flag which is UI state
        const cleanPalette = (p: ColorsParameterMap) =>
            Object.fromEntries(Object.entries(p).map(([k, v]) => [k, { codigo: v?.codigo }]));

        const current = JSON.stringify(cleanPalette(palette));
        const persisted = JSON.stringify(cleanPalette(persistedPalette));
        const isDirty = current !== persisted;

        onDirtyChange?.(isDirty);
    }, [palette, persistedPalette, onDirtyChange]);

    const handleSave = React.useCallback((colors: ColorsParameterMap) => {
        setPalette(clonePalette(colors));
    }, []);

    const handleOk = async (additionalSettings: Partial<SiteSettings> = {}) => {
        try {
            setIsSavingAppearance(true);
            if (!canManageColors) {
                message.error('No tenés permisos para modificar los colores.');
                throw new Error('appearance_permission_denied');
            }

            // Construimos el payload EXACTO que espera el backend
            const siteColorsPayload = Object.fromEntries(
                Object.entries(palette).map(([key, value]) => [
                    key,
                    { codigo: value?.codigo ?? '' },
                ]),
            );

            logInfo('Colores a guardar (payload site_colors): ', siteColorsPayload);
            logInfo('Harmony config a guardar: ', currentHarmonyConfig);

            await dispatch(
                updateSiteSettings({
                    ...additionalSettings,
                    site_colors: siteColorsPayload as ColorsParameterMap,
                    site_colors_harmony: currentHarmonyConfig ?? undefined,
                }),
            ).unwrap();

            if (prevAspect !== item_aspect_card) {
                setPrevAspect(item_aspect_card);
            }
            if (!isEmbedded) {
                setIsModalOpen(false);
            }
            setPersistedPalette(clonePalette(palette));
            message.success('Estilo guardado correctamente.');
            onAfterSave?.();
        } catch (error) {
            logInfo('Error al guardar los colores', error);
            message.error('No se pudo guardar el estilo.');
            throw error;
        } finally {
            setIsSavingAppearance(false);
        }
    };

    const handleCancel = () => {
        if (!isEmbedded) {
            setIsModalOpen(false);
        }
    };

    const handleRestore = () => {
        if (persistedPalette && Object.keys(persistedPalette).length > 0) {
            const cloned = clonePalette(persistedPalette);
            setPalette(cloned);
            applyPaletteToDom(cloned);
            dispatch(setAspectRadio(prevAspect));
            void message.info('Se restauraron los estilos publicados.');
        } else {
            const merged = mergePaletteWithRemote({}, siteColors ?? null);
            setPalette(merged);
            setPersistedPalette(clonePalette(merged));
            applyPaletteToDom(merged);
        }
    };

    // Exponer métodos via ref para guardado externo (botón Publicar)
    useImperativeHandle(ref, () => ({
        saveColors: handleOk,
        restoreColors: handleRestore,
    }), [handleOk, handleRestore]);

    const embeddedContent = (
        <div className={className} role="group" aria-label="Configuración de paleta">
            <div className="d-flex justify-content-center">
                <PanelConfigEstilo
                    onSave={handleSave}
                    initialPalette={persistedPalette}
                    initialHarmonyConfig={siteHarmonyConfig as ColorPaletteConfig | null}
                    onHarmonyChange={setCurrentHarmonyConfig}
                />
            </div>
        </div>
    );

    return (
        <>
            {!isEmbedded && (
                <Button
                    type="text"
                    onClick={showModal}
                    shape="circle"
                    className="w_050px h_050px border shadow bg-white p-1 border-dark"
                >
                    <PaletaColoresSegmentada strokeWidth={4} />
                </Button>
            )}
            {isEmbedded ? (
                embeddedContent
            ) : (
                <Modal
                    title="Personalizar"
                    open={isModalOpen}
                    onOk={() => void handleOk().catch(() => undefined)}
                    onCancel={handleCancel}
                    okButtonProps={{ loading: isSavingAppearance, disabled: !canManageColors }}
                >
                    <p>
                        Edita la apariencia del sitio y podes desplazarte por la página usando la ruedita del mouse o deslizando
                        con el dedo para ver los cambios en vivo.
                    </p>
                    <p>Estos no se verán reflejados para los demás hasta que presiones en confirmar.</p>

                    <p className="fw-bold mb-0">Paleta de Colores</p>
                    <p>Usamos una combinación de colores claros y oscuros para definir la apariencia del sitio.</p>
                    <div className="d-flex justify-content-center">
                        <PanelConfigEstilo onSave={handleSave} initialPalette={persistedPalette} />
                    </div>
                    <p className="fw-bold mb-0">Formato de Imagenes de Producto</p>
                    <div className="d-flex justify-content-center">
                        <AspectRatioSelect
                            initialAspect={item_aspect_card}
                            onChange={(value) => {
                                dispatch(setAspectRadio(value));
                            }}
                        />
                    </div>
                </Modal>
            )}
        </>
    );
});

export const HorariosParameterEdit: React.FC = () => {
    const nombreParametro = 'HORARIOS';
    const [horariosParameterData, setHorariosParameterData] = useState<ParameterType<HorariosDisponiblesType>>({ nombre: nombreParametro, valor: {} });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        const getParameterData = async () => {
            try {
                const responseParameter = await conns<ParameterType<HorariosDisponiblesType>>('settings:parameter_handler', [nombreParametro]).get();
                if (responseParameter.data && responseParameter.data.nombre) {
                    setHorariosParameterData(responseParameter.data);
                    form.setFieldsValue(responseParameter.data.valor);
                }
            } catch (error) {
                logInfo(`Error al leer el parámetro ${nombreParametro}`, error);
            }
        }
        void getParameterData();
    }, [form, nombreParametro]);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const updatedValues = Object.keys(values).reduce((acc: HorariosDisponiblesType, key: string) => {
                acc[parseInt(key)] = values[key] ? values[key].map((range: [Dayjs, Dayjs]) => [range[0].hour(), range[1].hour()]) : undefined;
                return acc;
            }, {});
            const updatedData = { ...horariosParameterData, valor: updatedValues };
            await conns<ParameterType<HorariosDisponiblesType>>('settings:parameter_handler', [nombreParametro]).put(updatedData);
            message.success('Horarios actualizados correctamente.');
            setHorariosParameterData(updatedData);
            setIsModalOpen(false);
        } catch (error) {
            logInfo(`Error al guardar el parámetro ${nombreParametro}`, error);
            message.error('Error al guardar los horarios.');
        }
    };

    const renderDayFormItems = () => {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

        return daysOfWeek.map((day, index) => (
            <Form.List key={day} name={index.toString()}>
                {(fields, { add, remove }) => (
                    <>
                        <Row gutter={16}>
                            <Col span={4}><strong>{day}</strong></Col>
                            <Col span={20}>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row key={key} gutter={16}>
                                        <Col span={10}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 0]}
                                                rules={[{ required: true, message: 'Seleccione el horario de inicio' }]}
                                            >
                                                <TimePicker format="HH"
                                                    placeholder="Desde"
                                                    showNow={false}
                                                    needConfirm={false}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 1]}
                                                rules={[{ required: true, message: 'Seleccione el horario de fin' }]}
                                            >
                                                <TimePicker format="HH"
                                                    placeholder="Hasta"
                                                    showNow={false}
                                                    needConfirm={false}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Button type="link" onClick={() => remove(name)}>Eliminar</Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button type="dashed" onClick={() => add()} block>Agregar rango de horario</Button>
                            </Col>
                        </Row>
                        <hr />
                    </>
                )}
            </Form.List>
        ));
    };

    return (
        <>
            <Button type="primary" onClick={showModal}>Editar Horarios</Button>
            <Modal title="Editar Horarios Disponibles" open={isModalOpen} onOk={handleSave} onCancel={handleCancel} width={800}>
                <Form form={form} layout="vertical">
                    {renderDayFormItems()}
                </Form>
            </Modal>
        </>
    );
}
